import { createClient } from '@supabase/supabase-js'

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL!
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY!

// On the shared FreeWebsites project Bells Rink lives in the `bells` schema with
// `bells-` prefixed buckets. Unset, these fall back to the original names.
const dbSchema = process.env.REACT_APP_SUPABASE_DB_SCHEMA || 'public'
export const PHOTOS_BUCKET = process.env.REACT_APP_SUPABASE_PHOTOS_BUCKET || 'photos'
const COMMUNITY_BUCKET = process.env.REACT_APP_SUPABASE_COMMUNITY_BUCKET || 'community-uploads'

export const supabase = createClient<any, any>(supabaseUrl, supabaseAnonKey, {
  db: { schema: dbSchema },
})

export type Photo = {
  id: string
  title: string
  description: string | null
  category: string
  storage_path: string
  url: string
  width: number | null
  height: number | null
  sort_order: number
  is_visible: boolean
  created_at: string
}

/**
 * Fetch all visible photos, optionally filtered by category.
 */
export async function getPhotos(category?: string): Promise<Photo[]> {
  let query = supabase
    .from('photos')
    .select('*')
    .eq('is_visible', true)
    .order('sort_order', { ascending: true })

  if (category) {
    query = query.eq('category', category)
  }

  const { data, error } = await query
  if (error) throw error
  return data ?? []
}

/**
 * Get a public URL for a photo in the "photos" storage bucket.
 */
export function getPhotoUrl(storagePath: string): string {
  const { data } = supabase.storage.from(PHOTOS_BUCKET).getPublicUrl(storagePath)
  return data.publicUrl
}

/**
 * Upload a community photo to the "community-uploads" storage bucket.
 * Returns the storage path on success.
 */
export async function uploadCommunityPhoto(
  file: File,
  uploaderName?: string
): Promise<string> {
  const timestamp = Date.now()
  const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_')
  const path = `${timestamp}_${safeName}`

  const { error } = await supabase.storage
    .from(COMMUNITY_BUCKET)
    .upload(path, file, {
      cacheControl: '3600',
      upsert: false,
    })

  if (error) throw error

  // Optionally log the upload metadata to a table
  try {
    await supabase.from('community_photos').insert({
      storage_path: path,
      uploader_name: uploaderName || 'Anonymous',
      file_name: file.name,
      file_size: file.size,
      status: 'pending', // pending review
    })
  } catch {
    // Metadata insert is best-effort; the file is already uploaded
  }

  return path
}

/**
 * Fetch distinct photo categories.
 */
export async function getCategories(): Promise<string[]> {
  const { data, error } = await supabase
    .from('photos')
    .select('category')
    .eq('is_visible', true)

  if (error) throw error
  const seen: Record<string, boolean> = {}
  const unique: string[] = []
  for (const r of data ?? []) {
    if (!seen[r.category]) {
      seen[r.category] = true
      unique.push(r.category)
    }
  }
  return unique.sort()
}
